import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Play, 
  ArrowLeft,
  Database,
  Users,
  Shield,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ActionCard from '../components/admin/ActionCard';
import ResultCard from '../components/admin/ResultCard';
import { 
  runTutorVisibilityMigration, 
  checkMigrationStatus, 
  seedSampleTutors 
} from '../utils/adminApi';

/**
 * Admin Migrations Panel - Refactored for Code Quality
 * 
 * Reduced complexity by:
 * - Extracting reusable components (ActionCard, ResultCard)
 * - Using centralized API utility (adminApi.js)
 * - Replacing XMLHttpRequest with standard fetch()
 * - Breaking down large functions into focused handlers
 */
const AdminMigrationsPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [statusResult, setStatusResult] = useState(null);
  const [seedResult, setSeedResult] = useState(null);

  const handleSeedTutors = async () => {
    setLoading(true);
    setSeedResult(null);
    
    try {
      const result = await seedSampleTutors();
      setSeedResult(result);
      toast.success(result.message || 'Tutors seeded successfully!');
    } catch (error) {
      const errorResult = { 
        success: false, 
        error: error.message 
      };
      setSeedResult(errorResult);
      toast.error(`Seed failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRunMigration = async () => {
    setLoading(true);
    setMigrationResult(null);
    
    try {
      const result = await runTutorVisibilityMigration();
      setMigrationResult(result);
      toast.success(`Migration complete! ${result.modified_count || 0} tutors updated.`);
    } catch (error) {
      const errorResult = { 
        success: false, 
        error: error.message 
      };
      setMigrationResult(errorResult);
      toast.error(`Migration failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    setLoading(true);
    setStatusResult(null);
    
    try {
      const result = await checkMigrationStatus();
      setStatusResult(result);
      toast.success('Status check complete');
    } catch (error) {
      const errorResult = { 
        success: false, 
        error: error.message 
      };
      setStatusResult(errorResult);
      toast.error(`Status check failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <div className="premium-gradient text-white py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="heading-lg mb-2 flex items-center gap-3">
                <Shield className="w-8 h-8" />
                Admin Control Panel
              </h1>
              <p className="opacity-90">Database migrations and system management</p>
            </div>
            <Button
              onClick={() => navigate('/admin/applications')}
              className="bg-white/20 hover:bg-white/30 backdrop-blur"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          
          {/* Seed Tutors */}
          <ActionCard
            icon={Users}
            title="Seed Sample Tutors"
            description="Add 5 sample tutors to your database. Use this if the directory is empty."
            buttonText="Seed Tutors"
            buttonIcon={Users}
            loadingText="Seeding..."
            onClick={handleSeedTutors}
            loading={loading}
            accentColor="var(--accent-gold)"
          >
            <ResultCard result={seedResult} type="seed" />
          </ActionCard>
          
          {/* Tutor Visibility Migration */}
          <ActionCard
            icon={RefreshCw}
            title="Run Migration"
            description="Update tutor visibility settings. Sets all approved tutors to public display."
            buttonText="Run Migration"
            buttonIcon={Play}
            loadingText="Migrating..."
            onClick={handleRunMigration}
            loading={loading}
            accentColor="rgb(20, 184, 166)"
          >
            <ResultCard result={migrationResult} type="migration" />
          </ActionCard>
          
          {/* Check Database Status */}
          <ActionCard
            icon={Database}
            title="Check Database Status"
            description="View current database statistics and tutor visibility counts."
            buttonText="Check Status"
            buttonIcon={Database}
            loadingText="Checking..."
            onClick={handleCheckStatus}
            loading={loading}
            accentColor="rgb(59, 130, 246)"
          >
            <ResultCard result={statusResult} type="status" />
          </ActionCard>
          
        </div>

        {/* Information Panel */}
        <div className="premium-card p-6 space-y-4">
          <h2 className="heading-md mb-4">About Migrations</h2>
          
          <div className="space-y-3 text-sm" style={{ color: 'var(--text-grey)' }}>
            <div>
              <strong className="text-black">Seed Sample Tutors:</strong>
              <p className="ml-4">Creates 5 sample tutor profiles with realistic data for testing and development.</p>
            </div>
            
            <div>
              <strong className="text-black">Tutor Visibility Migration:</strong>
              <p className="ml-4">Updates the <code className="bg-gray-100 px-2 py-1 rounded">isPublic</code> field for all tutor profiles. 
              Approved tutors are set to public (visible in directory).</p>
            </div>
            
            <div>
              <strong className="text-black">Database Status:</strong>
              <p className="ml-4">Shows real-time statistics including total tutors, visibility breakdown, and active student count.</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Important:</strong> Always check database status before running migrations. 
              Migrations modify live data and cannot be automatically reversed.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminMigrationsPanel;
